<template>
  <section class="directory">
    <header class="directory-head"><div><h2>Projects &amp; Divisions</h2><p>Organization-wide directory · includes records without staff or targets · deleted records excluded</p></div><button type="button" aria-label="Close directory" @click="$emit('close')">✕</button></header>
    <div class="directory-tabs" role="tablist" aria-label="Directory type">
      <button type="button" role="tab" :aria-selected="tab === 'project'" :class="{selected: tab === 'project'}" @click="changeTab('project')">Projects ({{ projects.length }})</button>
      <button type="button" role="tab" :aria-selected="tab === 'division'" :class="{selected: tab === 'division'}" @click="changeTab('division')">Divisions ({{ divisions.length }})</button>
    </div>
    <div class="directory-tools">
      <label>Search<input v-model="search" type="search" placeholder="Name, short code or division" /></label>
      <label v-if="tab === 'project'">Division<select v-model="divisionFilter"><option value="">All divisions</option><option value="unassigned">Unassigned / unavailable</option><option v-for="d in divisions" :key="d.id" :value="d.id">{{ d.code ? d.code + ' · ' : '' }}{{ d.name }}</option></select></label>
      <button type="button" :disabled="loading || saving" @click="fetchDirectory">Refresh</button>
    </div>
    <p class="directory-help">Use Edit to assign a short code (up to 20 letters, numbers, hyphens or underscores). Leave blank to clear it.</p>
    <p v-if="error" class="directory-error" role="alert">{{ error }}</p>
    <p v-if="notice" class="directory-notice" role="status">{{ notice }}</p>
    <div v-if="loading" class="directory-empty">Loading directory…</div>
    <div v-else class="directory-table-wrap">
      <table>
        <thead><tr><th>{{ tab === 'project' ? 'Project' : 'Division' }}</th><th>Short code</th><th v-if="tab === 'project'">Corresponding division</th><th v-else>Projects</th><th>Status</th></tr></thead>
        <tbody>
          <tr v-for="row in visibleRows" :key="row.id">
            <td><strong>{{ row.name || 'Unnamed' }}</strong><div v-if="tab === 'project' && row.location" class="directory-secondary">{{ row.location }}</div></td>
            <td><form v-if="editingId === row.id" class="directory-code-form" @submit.prevent="saveCode(row)"><input v-model="draftCode" :aria-label="'Short code for ' + row.name" maxlength="20" :disabled="saving" /><button :disabled="saving" type="submit">{{ saving ? 'Saving…' : 'Save' }}</button><button :disabled="saving" type="button" @click="editingId = null">Cancel</button></form><div v-else class="directory-code"><span>{{ row.code || 'Not set' }}</span><button type="button" :disabled="saving" :aria-label="'Edit short code for ' + row.name" @click="editCode(row)">Edit</button></div></td>
            <td v-if="tab === 'project'"><strong>{{ row.divisionName }}</strong><div class="directory-secondary">{{ row.divisionCode || 'No division code' }}</div></td>
            <td v-else><button type="button" class="directory-project-count" @click="showProjects(row)">{{ row.projectCount }} projects →</button></td>
            <td>{{ row.status || 'Not recorded' }}</td>
          </tr>
          <tr v-if="!visibleRows.length"><td colspan="4" class="directory-empty">{{ error ? 'Directory unavailable. Use Refresh to retry.' : 'No records match your search.' }}</td></tr>
        </tbody>
      </table>
    </div>
    <footer>{{ visibleRows.length }} of {{ tab === 'project' ? projects.length : divisions.length }} {{ tab === 'project' ? 'projects' : 'divisions' }}</footer>
  </section>
</template>

<script>
import axios from 'axios';
export default {
  name: 'CeoDirectory',
  data() { return { tab: 'project', projects: [], divisions: [], search: '', divisionFilter: '', loading: false, saving: false, error: '', notice: '', editingId: null, draftCode: '' }; },
  computed: {
    visibleRows() {
      const query = this.search.trim().toLowerCase();
      const divisionIds = new Set(this.divisions.map(d => d.id));
      return (this.tab === 'project' ? this.projects : this.divisions).filter(row => {
        if (this.tab === 'project' && this.divisionFilter) {
          if (this.divisionFilter === 'unassigned' ? divisionIds.has(row.divisionId) : row.divisionId !== this.divisionFilter) return false;
        }
        return !query || [row.name, row.code, row.divisionName, row.divisionCode].filter(Boolean).join(' ').toLowerCase().includes(query);
      });
    },
  },
  mounted() { this.fetchDirectory(); },
  methods: {
    headers() { return { 'x-auth-token': localStorage.getItem('token') }; },
    changeTab(tab) { if (this.saving) return; this.tab = tab; this.editingId = null; this.search = ''; this.error = ''; this.notice = ''; },
    showProjects(row) { this.changeTab('project'); if (!this.saving) this.divisionFilter = row.id; },
    editCode(row) { this.editingId = row.id; this.draftCode = row.code; this.error = ''; this.notice = ''; },
    async fetchDirectory() {
      this.loading = true; this.error = ''; this.editingId = null;
      try {
        const { data } = await axios.get('dashboard/ceo/directory', { headers: this.headers() });
        if (!data || !data.status) throw new Error((data && data.msg) || 'Unable to load directory.');
        this.projects = data.projects || []; this.divisions = data.divisions || [];
      } catch (e) { this.error = (e.response && e.response.data && e.response.data.msg) || e.message || 'Unable to load directory.'; }
      finally { this.loading = false; }
    },
    async saveCode(row) {
      if (this.saving) return;
      this.saving = true; this.error = ''; this.notice = '';
      const type = this.tab;
      try {
        const { data } = await axios.post('dashboard/ceo/directory/code', { type, id: row.id, code: this.draftCode }, { headers: this.headers() });
        if (!data || !data.status) throw new Error((data && data.msg) || 'Unable to save short code.');
        row.code = data.code;
        if (type === 'division') this.projects.forEach(project => { if (project.divisionId === row.id) project.divisionCode = data.code; });
        this.editingId = null; this.notice = 'Short code saved for ' + row.name + '.';
      } catch (e) { this.error = (e.response && e.response.data && e.response.data.msg) || e.message || 'Unable to save short code.'; }
      finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
.directory { background: #fff; color: #253c30; border-radius: 14px; }
.directory-head { display: flex; justify-content: space-between; align-items: start; padding: 22px 24px; border-bottom: 1px solid #e3ebe5; }
.directory h2 { font-size: 21px; margin: 0 0 5px; }
.directory-head p, .directory-help { font-size: 12px; color: #63766b; margin: 0; }
.directory button { border: 1px solid #cedcd2; border-radius: 6px; padding: 7px 12px; color: #14663a; background: #f7faf8; cursor: pointer; }
.directory button:disabled { opacity: .5; cursor: wait; }
.directory button:focus-visible, .directory input:focus-visible, .directory select:focus-visible { outline: 2px solid #14663a; outline-offset: 2px; }
.directory-tabs { display: flex; gap: 8px; padding: 16px 24px 0; }
.directory-tabs .selected { background: #14663a; color: #fff; }
.directory-tools { display: flex; align-items: end; gap: 14px; padding: 16px 24px; flex-wrap: wrap; }
.directory-tools label { display: flex; flex-direction: column; gap: 5px; flex: 1 1 200px; font-size: 12px; }
.directory input, .directory select { border: 1px solid #cedcd2; border-radius: 6px; padding: 9px; background: #fff; min-width: 0; }
.directory-help, .directory-error, .directory-notice { padding: 0 24px 12px; }
.directory-error { color: #b42318; } .directory-notice { color: #14663a; }
.directory-table-wrap { overflow: auto; max-height: 54vh; }
.directory table { border-collapse: collapse; width: 100%; min-width: 760px; text-align: left; font-size: 13px; }
.directory th { position: sticky; top: 0; background: #edf4ef; padding: 12px 24px; font-size: 11px; text-transform: uppercase; }
.directory td { border-bottom: 1px solid #edf2ee; padding: 14px 24px; vertical-align: top; }
.directory-secondary { color: #6b7f72; margin-top: 5px; font-size: 12px; }
.directory-code, .directory-code-form { display: flex; gap: 8px; align-items: center; }
.directory-code-form input { width: 130px; text-transform: uppercase; }
.directory-code span { min-width: 70px; }
.directory-empty { padding: 24px; text-align: center; color: #6b7f72; }
.directory footer { padding: 15px 24px; font-size: 12px; color: #6b7f72; }
</style>
